/* =============================================================================
   VIEWS / ADMIN / DASHBOARD-LISTS — expandierbare Listen + Anfragen-Sicht
   =============================================================================
   S202 (REF-8) — aus dashboard.js extrahiert. Die „Anfragen"-Sicht der
   kombinierten „Als Nächstes ⇄ Anfragen"-Karte + der In-Card-Expand-Mechanismus:
     - DASHBOARD_LIST_LIMIT, renderExpandableDashList
     - dashboardExpandList / dashboardCollapseList (Visible-Count-Store)
     - sortPendingRequests, renderRequestsBody, renderOrderListItem, renderItem

   Reine Extraktion, NULL Verhaltensänderung. Der Visible-Count-State liegt im
   paginated-list-Store (überlebt Re-Renders). Rück-Ruf auf ein volles
   renderDashboard() läuft über requestDashboardRerender (data) — kein Import des
   Orchestrators (acyclisch). Inline-Handler bleiben `func()`.

   Externe Helper, die noch in app.js leben: renderReservationItem (Listen-Item)
   — über window.* aufgelöst.
============================================================================ */

import { htmlEscape, todayISO, dayNum, monthShort } from '../../utils/helpers.js';
import { getVisibleCount, setVisibleCount, resetVisibleCount } from '../../core/paginated-list.js';
import { requestDashboardRerender } from './dashboard-data.js';

function renderItem(r) {
  if (typeof window.renderReservationItem === 'function') return window.renderReservationItem(r);
  return `<div class="placeholder">${htmlEscape(r.name)}</div>`;
}

/** Dashboard-Listen-Karte mit max-5-Limit + «N weitere ansehen»-In-Card-Expand. */
const DASHBOARD_LIST_LIMIT = 5;

/**
 * S108 — In-Card-Expand: rendert die ersten `getVisibleCount` Items einer
 * Dashboard-Liste + einen CSP-konformen „N weitere ansehen"-Button, der die
 * sichtbare Anzahl erhöht und das Dashboard neu rendert (statt — wie früher —
 * per `switchTab('reservations')` in die abgeschaffte Listen-Sicht zu springen).
 * Der Visible-Count-State liegt in paginated-list (überlebt Re-Renders).
 */
function renderExpandableDashList(items, containerId, renderItemFn) {
  const total   = items.length;
  const visible = Math.min(getVisibleCount(containerId, DASHBOARD_LIST_LIMIT), total);
  const shown   = items.slice(0, visible);
  const rest    = total - visible;
  let footer = '';
  if (rest > 0) {
    footer = `
      <button class="dash-overflow" onclick="dashboardExpandList('${containerId}', ${DASHBOARD_LIST_LIMIT}, ${total})">
        ${rest} weitere ansehen →
      </button>`;
  } else if (total > DASHBOARD_LIST_LIMIT) {
    footer = `
      <button class="dash-overflow" onclick="dashboardCollapseList('${containerId}')">
        weniger zeigen ↑
      </button>`;
  }
  return `<div>${shown.map(renderItemFn).join('')}</div>${footer}`;
}

/** Erweitert die sichtbare Anzahl einer Dashboard-Liste + re-rendert (CSP: func()). */
export function dashboardExpandList(containerId, step, total) {
  const cur = getVisibleCount(containerId, DASHBOARD_LIST_LIMIT);
  let next = cur + (Number(step) || DASHBOARD_LIST_LIMIT);
  if (total != null && next > total) next = total;
  setVisibleCount(containerId, next);
  requestDashboardRerender();
}

/** Klappt eine Dashboard-Liste wieder auf das Default-Limit ein + re-rendert. */
export function dashboardCollapseList(containerId) {
  resetVisibleCount(containerId);
  requestDashboardRerender();
}

/**
 * S109 — pending-Reservierungen sortiert: aufsteigend nach Datum+Uhrzeit
 * (dringendste Entscheidung oben; heute/morgen zuerst, künftige danach).
 */
function sortPendingRequests(pending) {
  return (pending || []).slice().sort((a, b) =>
    ((a.date || '') + (a.time || '')).localeCompare((b.date || '') + (b.time || ''))
  );
}

/**
 * S109/S192 — Body der „Anfragen"-Sicht: offene Bestellungen (`status==='pending'`,
 * vom Kunden gesendet, noch nicht angenommen) UND ALLE offenen Reservierungs-
 * anfragen (`status==='pending'`, **alle Tage**). Beide je Zeile **einzeln**
 * annehmen/ablehnen (kein Bulk, Jonas „immer einzeln"). Orders zuerst (frisch
 * eingegangen, brauchen Annahme), darunter die Reservierungs-Anfragen über den
 * S108-Expand-Mechanismus (eigener Listen-Key `dash-requests-list`). Ruhiger
 * Empty-State, nur wenn beide leer sind (kein „mal-da-mal-weg").
 */
function renderRequestsBody(pending, pendingOrders) {
  const orders = pendingOrders || [];
  const sorted = sortPendingRequests(pending);
  if (orders.length === 0 && sorted.length === 0) {
    return `<div class="empty">Keine offenen Anfragen.</div>`;
  }
  const ordersHtml = orders.length
    ? `<div class="dash-requests-orders">${orders.map(renderOrderListItem).join('')}</div>`
    : '';
  const resHtml = sorted.length
    ? renderExpandableDashList(sorted, 'dash-requests-list', renderItem)
    : '';
  return ordersHtml + resHtml;
}

/**
 * Phase 19.3 (Session 22 Funktionstest): Dashboard-Order-Card im selben
 * .res-item-Stil wie Reservierungen, damit Dashboard visuell konsistent ist.
 * Datums-Block links (Tag/Monat + Abholzeit), Hauptbereich mit Name +
 * Items-Summary + Badges, Aktions-Buttons rechts. Klick auf den meta-Bereich
 * öffnet Order-Detail.
 */
function renderOrderListItem(o) {
  const num         = htmlEscape(o.orderNumber || o.id.slice(-6));
  const name        = htmlEscape(o.customerName || '—');
  const phone       = htmlEscape(o.customerPhone || '');
  const email       = htmlEscape(o.customerEmail || '');
  const pickupDate  = o.pickupDate || todayISO();
  const pickupTime  = String(o.pickupTime || '').slice(0, 5);
  const total       = ((Number(o.totalGrossCents) || 0) / 100)
                        .toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  // S192-Fix — Service-Typ 3-wertig (Dine-In / Lieferung / Abholung). Vorher fiel
  // Delivery in den else-Zweig → fälschlich „🥡 Abholung". Icons/Labels wie Timeline.
  const svc = o.serviceType || 'takeaway';
  const svcBadge = svc === 'dine-in'
    ? { cls: 'badge-confirmed', label: '🍽️ Hier Essen' }
    : svc === 'delivery'
      ? { cls: 'badge-takeaway', label: '🛵 Lieferung' }
      : { cls: 'badge-takeaway', label: '🥡 Abholung' };
  // Items-Count via preorder_snapshot (Header-only) — items werden lazy nachgeladen
  const itemCount = (o.preorderSnapshot && Array.isArray(o.preorderSnapshot.items))
    ? o.preorderSnapshot.items.reduce((s, it) => s + (Number(it.qty) || 1), 0)
    : null;
  return `
    <div class="res-item ${o.status}">
      <div class="res-date">
        <div class="day">${dayNum(pickupDate)}</div>
        <div class="month">${monthShort(pickupDate)}</div>
        <div class="time">${pickupTime || '—'}</div>
      </div>
      <div class="res-main">
        <div class="name">${name} · Best.-Nr. ${num}</div>
        <div class="meta">
          ${phone ? htmlEscape(phone) + ' · ' : ''}${email ? htmlEscape(email) + ' · ' : ''}
          <strong>${total} €</strong>
        </div>
        <div class="meta-tags">
          <span class="badge badge-${o.status}">offen</span>
          <span class="badge ${svcBadge.cls}">${svcBadge.label}</span>
          ${itemCount != null ? `<span class="badge badge-completed">${itemCount} Position${itemCount !== 1 ? 'en' : ''}</span>` : ''}
        </div>
        ${o.notes ? `<div class="notes">📝 ${htmlEscape(o.notes)}</div>` : ''}
      </div>
      <div class="res-actions">
        <button class="btn btn-primary btn-sm" onclick="orderSetStatus('${htmlEscape(o.id)}','confirmed')">Annehmen</button>
        <button class="btn btn-danger btn-sm" onclick="orderCancel('${htmlEscape(o.id)}')">Ablehnen</button>
        <button class="btn btn-ghost btn-sm" onclick="openOrderDetail('${htmlEscape(o.id)}')">Details</button>
      </div>
    </div>
  `;
}

// renderRequestsBody + sortPendingRequests werden von dashboard-timeline
// (renderNextPane) genutzt → als benannte Exports bereitgestellt.
export { renderExpandableDashList, sortPendingRequests, renderRequestsBody, renderOrderListItem, renderItem, DASHBOARD_LIST_LIMIT };

// Backward-Compat — die beiden Expand-Handler sind window-gebunden (Inline-onclick).
if (typeof window !== 'undefined') {
  Object.assign(window, { dashboardExpandList, dashboardCollapseList });
}
